import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { useDialog } from '../context/DialogContext'
import { Buildings, CaretDown, Check, ArrowsClockwise, Sparkle, EnvelopeSimple, UserPlus } from '@phosphor-icons/react'
import { getMyInvites, acceptInvite as acceptInviteApi, PendingInvite } from '../services/auth'

interface CompanySwitcherProps {
  className?: string
}

export function CompanySwitcher({ className = '' }: CompanySwitcherProps) {
  const { user, changeCurrentCompany, refreshProfile } = useAuth()
  const { showError } = useDialog()
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [toast, setToast] = useState<{ message: string; show: boolean }>({ message: '', show: false })
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([])
  const [loadingInvites, setLoadingInvites] = useState(false)
  const [acceptingInvite, setAcceptingInvite] = useState<string | null>(null)

  // Buscar convites pendentes ao carregar e quando o dropdown abre
  useEffect(() => {
    if (user) {
      fetchInvites()
    }
  }, [user])

  // Atualizar convites quando abre o dropdown (para ter dados frescos)
  useEffect(() => {
    if (isOpen && user) {
      fetchInvites()
    }
  }, [isOpen])

  const fetchInvites = async () => {
    try {
      setLoadingInvites(true)
      const { invites } = await getMyInvites()
      setPendingInvites(invites)
    } catch (error) {
      console.error('Erro ao buscar convites:', error)
    } finally {
      setLoadingInvites(false)
    }
  }

  const getCompanyInitials = (name?: string) => {
    if (!name) return '??'
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  if (!user) {
    return null
  }

  const companies = user.companies

  // Enrich currentCompany with data from companies list if missing
  const rawCurrentCompany = user.current_company
  const currentCompany = rawCurrentCompany ? {
    ...rawCurrentCompany,
    name: rawCurrentCompany.name || companies.find(c => c.id === rawCurrentCompany.id)?.name,
    logo_url: rawCurrentCompany.logo_url || companies.find(c => c.id === rawCurrentCompany.id)?.logo_url,
  } : undefined

  const hasInvitesOrCompanies = companies.length > 0 || pendingInvites.length > 0

  // Se não tem empresas nem convites, não mostra nada
  if (companies.length === 0 && !isOpen) {
    return null
  }

  // Se só tem 1 empresa e não tem convites pendentes, mostra apenas um label simples
  if (companies.length === 1 && currentCompany && pendingInvites.length === 0 && !isOpen) {
    return (
      <div className={`${className}`}>
        <div className="flex items-center gap-3 px-4 py-3 bg-white/10 rounded-xl">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-400 to-slate-600 flex items-center justify-center flex-shrink-0 overflow-hidden">
            {currentCompany.logo_url ? (
              <img
                src={currentCompany.logo_url}
                alt={currentCompany.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-white font-bold text-sm">
                {getCompanyInitials(currentCompany.name)}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">
              {currentCompany.name}
            </p>
            <p className="text-xs text-white/60">
              {currentCompany.type === 'shipper' ? 'Embarcador' : 'Agente de Carga'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  const showToast = (message: string) => {
    setToast({ message, show: true })
    setTimeout(() => {
      setToast({ message: '', show: false })
    }, 3000)
  }

  const handleCompanyChange = async (companyId: string, companyType: 'shipper' | 'freight-forwarder', companyName: string) => {
    if (currentCompany?.id === companyId) {
      setIsOpen(false)
      return
    }

    try {
      setLoading(true)
      await changeCurrentCompany(companyId, companyType)
      setIsOpen(false)
      showToast(`✅ Empresa alterada para: ${companyName}`)

      // Redirect to the correct dashboard based on company type
      const targetPath = companyType === 'shipper'
        ? '/shipper/dashboard'
        : '/freight-forwarder/dashboard'

      // Pequeno delay para dar feedback visual e redirecionar
      setTimeout(() => {
        window.location.href = targetPath
      }, 500)
    } catch (error: any) {
      console.error('Erro ao trocar empresa:', error)
      showError('Erro', error.response?.data?.error || 'Erro ao trocar de empresa')
    } finally {
      setLoading(false)
    }
  }

  const handleAcceptInvite = async (invite: PendingInvite) => {
    try {
      setAcceptingInvite(invite.id)
      await acceptInviteApi(invite.token)

      // Refresh profile to get updated companies list
      await refreshProfile()

      setIsOpen(false)
      showToast(`🎉 Convite aceito! Bem-vindo à ${invite.companyName}`)

      // Redirect to the correct dashboard based on company type
      const targetPath = invite.companyType === 'shipper'
        ? '/shipper/dashboard'
        : '/freight-forwarder/dashboard'

      // Pequeno delay para dar feedback visual e redirecionar
      setTimeout(() => {
        window.location.href = targetPath
      }, 500)
    } catch (error: any) {
      console.error('Erro ao aceitar convite:', error)
      showError('Erro', error.response?.data?.error || 'Erro ao aceitar convite')
    } finally {
      setAcceptingInvite(null)
    }
  }

  const getCompanyBadgeColor = (type: 'shipper' | 'freight-forwarder') => {
    return type === 'shipper'
      ? 'bg-gradient-to-r from-blue-400 to-blue-600 text-white'
      : 'bg-gradient-to-r from-orange-400 to-orange-600 text-white'
  }

  const getCompanyTypeLabel = (type: 'shipper' | 'freight-forwarder') => {
    return type === 'shipper' ? '📦 Embarcador' : '🚢 Agente de Carga'
  }

  return (
    <>
      {/* Toast Notification */}
      {toast.show && (
        <div className="fixed top-20 right-4 z-50 animate-slide-in-right">
          <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-6 py-3 rounded-lg shadow-xl flex items-center gap-3">
            <Sparkle size={20} weight="fill" className="animate-pulse" />
            <p className="font-medium">{toast.message}</p>
          </div>
        </div>
      )}

      <div className={`relative ${className}`}>
        {/* Trigger Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          disabled={loading}
          className="w-full group relative overflow-hidden"
        >
          {/* Animated background on hover */}
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/10 to-emerald-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          <div className="relative flex items-center gap-3 px-4 py-3 bg-white border-2 border-gray-200 rounded-xl hover:border-emerald-400 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md">
            {/* Company Avatar with gradient */}
            <div className="relative">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center flex-shrink-0 shadow-md overflow-hidden">
                {currentCompany?.logo_url ? (
                  <img
                    src={currentCompany.logo_url}
                    alt={currentCompany.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-white font-bold text-sm">
                    {currentCompany ? getCompanyInitials(currentCompany.name) : '?'}
                  </span>
                )}
              </div>
              {/* Badge for pending invites */}
              {pendingInvites.length > 0 && (
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center animate-pulse">
                  <span className="text-white text-xs font-bold">{pendingInvites.length}</span>
                </div>
              )}
              {/* Pulse indicator when no invites */}
              {pendingInvites.length === 0 && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full animate-pulse" />
              )}
            </div>

            <div className="flex-1 text-left min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <p className="text-sm font-bold text-gray-900 truncate">
                  {currentCompany?.name || 'Selecione uma empresa'}
                </p>
                {loading && (
                  <ArrowsClockwise size={14} weight="bold" className="text-emerald-600 animate-spin" />
                )}
              </div>
              {currentCompany && (
                <p className="text-xs font-medium text-gray-500">
                  {getCompanyTypeLabel(currentCompany.type).replace(/[📦🚢]/g, '').trim()}
                </p>
              )}
            </div>

            <CaretDown
              size={18}
              weight="bold"
              className={`text-gray-400 group-hover:text-emerald-600 transition-all duration-300 ${isOpen ? 'rotate-180' : ''}`}
            />
          </div>
        </button>

        {/* Dropdown */}
        {isOpen && (
          <>
            {/* Overlay */}
            <div
              className="fixed inset-0 z-10"
              onClick={() => setIsOpen(false)}
            />

            {/* Menu with animation - grows wider when there are invites */}
            <div
              className={`absolute top-full left-0 mt-2 bg-white rounded-xl shadow-2xl border-2 border-gray-100 py-2 z-20 max-h-[28rem] overflow-y-auto animate-scale-in ${
                pendingInvites.length > 0 ? 'min-w-[340px]' : 'right-0'
              }`}
              style={{
                animation: 'scaleIn 0.2s ease-out'
              }}
            >
              {/* Pending Invites Section */}
              {pendingInvites.length > 0 && (
                <>
                  <div className="px-4 py-2.5 border-b border-amber-100 bg-amber-50">
                    <div className="flex items-center gap-2">
                      <EnvelopeSimple size={16} weight="bold" className="text-amber-600" />
                      <p className="text-xs font-bold text-amber-700 uppercase tracking-wider">
                        Convites Pendentes • {pendingInvites.length}
                      </p>
                    </div>
                  </div>
                  <div className="p-2 border-b border-gray-100">
                    {pendingInvites.map((invite, index) => (
                      <div
                        key={invite.id}
                        style={{
                          animationDelay: `${index * 50}ms`
                        }}
                        className="w-full px-3 py-3 rounded-lg bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 mb-2 last:mb-0 animate-fade-in-up"
                      >
                        <div className="flex items-start gap-3">
                          {/* Invite Icon */}
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-md">
                            <UserPlus size={24} weight="bold" className="text-white" />
                          </div>

                          {/* Invite Info */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-gray-900 truncate">
                              {invite.companyName}
                            </p>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              <span className={`text-xs px-2.5 py-1 rounded-full font-bold ${
                                getCompanyBadgeColor(invite.companyType)
                              } shadow-sm`}>
                                {getCompanyTypeLabel(invite.companyType)}
                              </span>
                              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-semibold">
                                {invite.role}
                              </span>
                            </div>
                            {invite.invitedByName && (
                              <p className="text-xs text-gray-500 mt-1.5">
                                Convidado por <span className="font-medium">{invite.invitedByName}</span>
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Accept Button */}
                        <button
                          onClick={() => handleAcceptInvite(invite)}
                          disabled={acceptingInvite === invite.id || loading}
                          className="w-full mt-3 px-4 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-bold text-sm rounded-lg transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                        >
                          {acceptingInvite === invite.id ? (
                            <>
                              <ArrowsClockwise size={16} weight="bold" className="animate-spin" />
                              Aceitando...
                            </>
                          ) : (
                            <>
                              <Check size={16} weight="bold" />
                              Aceitar Convite
                            </>
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Loading invites indicator */}
              {loadingInvites && pendingInvites.length === 0 && (
                <div className="px-4 py-3 border-b border-gray-100">
                  <div className="flex items-center gap-2 text-gray-500">
                    <ArrowsClockwise size={14} className="animate-spin" />
                    <span className="text-xs">Verificando convites...</span>
                  </div>
                </div>
              )}

              {/* Companies Section */}
              {companies.length === 0 && pendingInvites.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <Buildings size={40} className="mx-auto text-gray-300 mb-3" weight="duotone" />
                  <p className="text-sm text-gray-500 font-medium">Nenhuma empresa disponível</p>
                </div>
              ) : companies.length > 0 && (
                <>
                  <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                      {pendingInvites.length > 0 ? 'Minhas Empresas' : 'Trocar Empresa'} • {companies.length} {companies.length === 1 ? 'disponível' : 'disponíveis'}
                    </p>
                  </div>
                  <div className="p-2">
                    {companies.map((company, index) => {
                      const isActive = currentCompany?.id === company.id

                      return (
                        <button
                          key={company.id}
                          onClick={() => handleCompanyChange(company.id, company.type, company.name)}
                          disabled={loading}
                          style={{
                            animationDelay: `${(pendingInvites.length + index) * 50}ms`
                          }}
                          className={`
                            w-full px-3 py-3 rounded-lg transition-all duration-200 flex items-center gap-3 mb-1 last:mb-0
                            animate-fade-in-up
                            ${isActive
                              ? 'bg-gradient-to-r from-emerald-50 to-emerald-100 border-2 border-emerald-300 shadow-sm'
                              : 'hover:bg-gray-50 border-2 border-transparent hover:border-gray-200'
                            }
                            disabled:opacity-50 disabled:cursor-not-allowed
                            group
                          `}
                        >
                          {/* Company Avatar */}
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-200 group-hover:scale-110 overflow-hidden ${
                            isActive
                              ? 'bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-md'
                              : 'bg-gradient-to-br from-gray-300 to-gray-400'
                          }`}>
                            {company.logo_url ? (
                              <img
                                src={company.logo_url}
                                alt={company.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-white font-bold text-sm">
                                {getCompanyInitials(company.name)}
                              </span>
                            )}
                          </div>

                          {/* Company Info */}
                          <div className="flex-1 text-left min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <p className={`text-sm font-bold truncate transition-colors ${
                                isActive ? 'text-emerald-900' : 'text-gray-900 group-hover:text-emerald-700'
                              }`}>
                                {company.name}
                              </p>
                              {isActive && (
                                <div className="animate-bounce-subtle">
                                  <Check size={16} weight="bold" className="text-emerald-600 flex-shrink-0" />
                                </div>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`text-xs px-2.5 py-1 rounded-full font-bold ${
                                getCompanyBadgeColor(company.type)
                              } shadow-sm`}>
                                {getCompanyTypeLabel(company.type)}
                              </span>
                              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-semibold">
                                {company.role}
                              </span>
                            </div>
                            {company.trading_name && (
                              <p className="text-xs text-gray-400 mt-1.5 truncate font-medium">
                                {company.trading_name}
                              </p>
                            )}
                            {company.document && (
                              <p className="text-xs text-gray-400 mt-0.5 font-mono">
                                {company.document}
                              </p>
                            )}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes bounceSubtle {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
        }

        .animate-scale-in {
          animation: scaleIn 0.2s ease-out;
        }

        .animate-fade-in-up {
          animation: fadeInUp 0.3s ease-out;
          animation-fill-mode: both;
        }

        .animate-slide-in-right {
          animation: slideInRight 0.3s ease-out;
        }

        .animate-bounce-subtle {
          animation: bounceSubtle 1s ease-in-out infinite;
        }
      `}</style>
    </>
  )
}
